export interface SipTrunkEntry {
  name: string;
  host: string | null;
  /** Registration state, only present for the gateways that register. */
  state: string | null;
}

export interface SipTrunkSummary {
  trunks: SipTrunkEntry[];
  /** Proxy through which everything leaves, when the instance has one. */
  proxy: string | null;
}

/**
 * Reads back the summary Interstellar writes into `account.sip_trunk`, which
 * looks like `twilio.com (sip:brainhi.pstn.twilio.com); voipms (sip.voip.ms)
 * REGED via sip:kamailio-ecs.kamailio`. An account can carry seven or more
 * trunks, and Interstellar leads with the one it places calls through, so the
 * cell shows the first entry instead of the whole paragraph.
 *
 * Values typed by hand before the refresh existed ('voipms') parse as a single
 * trunk with no host, which is what they are.
 */
export function parseSipTrunk(value: string | null | undefined): SipTrunkSummary {
  if (!value) return { trunks: [], proxy: null };

  let body = value;
  let proxy: string | null = null;
  const via = body.lastIndexOf(' via ');
  if (via !== -1) {
    proxy = body.slice(via + ' via '.length).trim() || null;
    body = body.slice(0, via);
  }

  const trunks = body
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = /^(.*?)\s*\(([^)]*)\)\s*(.*)$/.exec(part);
      if (!match) return { name: part, host: null, state: null };
      return {
        name: match[1].trim() || part,
        host: match[2].trim() || null,
        state: match[3].trim() || null,
      };
    });

  return { trunks, proxy };
}

/**
 * Strips the formatting from a NANP number as an admin types it
 * ('(787) 555-1234', '+1 787 555 1234') and returns its digits, or null when it
 * isn't a 10-digit number with an optional leading 1. Parsing it as an integer
 * instead would stop at the first dash and save '787' as the number.
 */
export function parsePhoneNumber(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) return digits;
  return null;
}

/**
 * Normalizes a NANP number to E.164 ('+17875551234'). Interstellar renders the
 * account's main number verbatim as the outbound caller ID, and Twilio rejects
 * calls (403) whose caller ID lacks the country code.
 */
export function toE164(value: string): string | null {
  const digits = parsePhoneNumber(value);
  return digits ? `+1${digits.slice(-10)}` : null;
}
